import type { Persona } from '@repo/contracts';

/**
 * `@` mentions in the composer, the way Discord and Slack do them: the text
 * is plain, a mention is `@Handle` inside it, and who is asked is read back
 * out of the text when it is sent. No hidden state beside the text, so
 * deleting a mention by hand un-asks that person, exactly as it looks.
 */

type Mentionable = Pick<Persona, 'id' | 'name'>;

/** A letter, digit, `_` or `-` — what a handle is made of. */
const HANDLE = /[\p{L}\p{N}_-]/u;

function firstName(persona: Mentionable): string {
  return persona.name.trim().split(/\s+/)[0] ?? persona.name;
}

/**
 * What to type after `@` to mean this person: their first name, unless
 * someone else in the room shares it — then the full name run together
 * (`@MayaIqbal`), so every handle names exactly one person.
 */
export function handleOf(persona: Mentionable, all: Mentionable[]): string {
  const first = firstName(persona);
  const shared = all.some(
    (other) =>
      other.id !== persona.id &&
      firstName(other).toLowerCase() === first.toLowerCase(),
  );
  return shared ? persona.name.replace(/\s+/g, '') : first;
}

/**
 * The mention being typed at the caret, if any: an `@` at the start of the
 * text or after a space, followed by handle characters up to the caret.
 * An `@` inside a word (an email address) is not a mention.
 */
export function activeMention(
  text: string,
  caret: number,
): { query: string; start: number } | null {
  const before = text.slice(0, caret);
  const match = /(^|\s)@([\p{L}\p{N}_-]*)$/u.exec(before);
  if (!match) return null;
  const query = match[2] ?? '';
  return { query, start: caret - query.length - 1 };
}

/** People whose name or handle begins with what has been typed so far. */
export function matchMentions<T extends Mentionable>(
  query: string,
  personas: T[],
): T[] {
  const q = query.toLowerCase();
  if (!q) return personas;
  return personas.filter((persona) => {
    const words = persona.name.toLowerCase().split(/\s+/);
    return (
      handleOf(persona, personas).toLowerCase().startsWith(q) ||
      words.some((word) => word.startsWith(q))
    );
  });
}

/** Replaces the `@query` being typed with the chosen person's handle and a
 *  space, and says where the caret goes next. */
export function insertMention(
  text: string,
  mention: { query: string; start: number },
  handle: string,
): { caret: number; text: string } {
  const end = mention.start + 1 + mention.query.length;
  const rest = text.slice(end);
  const spacer = rest.startsWith(' ') ? '' : ' ';
  const inserted = `@${handle}${spacer}`;
  return {
    caret: mention.start + inserted.length + (spacer ? 0 : 1),
    text: text.slice(0, mention.start) + inserted + rest,
  };
}

export type MentionSegment =
  | { text: string; type: 'text' }
  | { persona: Mentionable; text: string; type: 'mention' };

/**
 * The text cut into plain runs and mentions of people in the room — for
 * drawing mentions highlighted, and for reading who was asked. An `@word`
 * that names nobody stays plain text.
 */
export function segmentMentions(
  text: string,
  personas: Mentionable[],
): MentionSegment[] {
  const byHandle = new Map(
    personas.map((persona) => [
      handleOf(persona, personas).toLowerCase(),
      persona,
    ]),
  );
  const segments: MentionSegment[] = [];
  let plain = '';
  let i = 0;

  while (i < text.length) {
    const atWordStart = i === 0 || /\s/.test(text[i - 1]!);
    if (text[i] === '@' && atWordStart) {
      let end = i + 1;
      while (end < text.length && HANDLE.test(text[end]!)) end++;
      const persona = byHandle.get(text.slice(i + 1, end).toLowerCase());
      if (persona) {
        if (plain) segments.push({ text: plain, type: 'text' });
        plain = '';
        segments.push({ persona, text: text.slice(i, end), type: 'mention' });
        i = end;
        continue;
      }
    }
    plain += text[i];
    i++;
  }
  if (plain) segments.push({ text: plain, type: 'text' });
  return segments;
}

/** Everyone mentioned, once each, in the order they were first mentioned. */
export function mentionedIds(text: string, personas: Mentionable[]): string[] {
  const ids: string[] = [];
  for (const segment of segmentMentions(text, personas)) {
    if (segment.type === 'mention' && !ids.includes(segment.persona.id)) {
      ids.push(segment.persona.id);
    }
  }
  return ids;
}

/** "Hey ", "So " — what a spoken address can start with. */
const GREETING = /^(?:(?:hey|hi|so|ok|okay)[,\s]+)?/i;

/**
 * A question that opens by naming who it is for — "Elena and Priya, how
 * would you…", the way Floto writes a suggestion aimed at part of the room —
 * rewritten to open with their mentions instead: "@Elena @Priya how would
 * you…". Mentions are what decide who is asked, so without this a question
 * meant for two goes to everyone.
 *
 * Only a leading address counts, and only when every name in it is someone
 * in the room; a name mid-sentence ("what did Elena mean…") is talked about,
 * not talked to, and is left alone.
 */
export function addressMentions(text: string, personas: Mentionable[]): string {
  const greeting = GREETING.exec(text)?.[0] ?? '';
  const rest = text.slice(greeting.length);
  const lookup = (name: string) =>
    personas.find(
      (persona) =>
        persona.name.toLowerCase() === name.toLowerCase() ||
        (handleOf(persona, personas).toLowerCase() === name.toLowerCase() &&
          firstName(persona).toLowerCase() === name.toLowerCase()),
    );

  // "Maya Khan, Marcus — which…" has a comma inside the address, so try
  // every place it could end and keep the longest that is only names.
  let best: { length: number; people: Mentionable[] } | null = null;
  for (const end of rest.matchAll(/\s*(?:,|:|—|–|\s-\s)\s*/gu)) {
    const names = rest
      .slice(0, end.index)
      .split(/\s*,\s*|\s+and\s+|\s*&\s*/i)
      .map((name) => name.trim());
    const people = names.map(lookup);
    if (names.some((name) => !name) || people.some((person) => !person)) {
      continue;
    }
    best = {
      length: end.index + end[0].length,
      people: people as Mentionable[],
    };
  }
  if (!best) return text;

  const mentions = best.people
    .filter((person, i, all) => all.indexOf(person) === i)
    .map((person) => `@${handleOf(person, personas)}`)
    .join(' ');
  return `${mentions} ${rest.slice(best.length)}`;
}
